import { t } from 'i18next';
import React, { useState, useEffect, useRef, ReactNode } from 'react';

interface FormSelectCreatableProps<T extends string> {
  label: string;
  labelElement?: ReactNode;
  value: T;
  onChange: (value: T) => void;
  options: { id: string; name: string }[];
  onCreateOption?: (option: string) => void;
  placeholder?: string;
  hideLabel?: boolean;
  createText?: string;
  disabled?: boolean;
}

export const FormSelectCreatable = <T extends string>({
  label,
  labelElement,
  value,
  onChange,
  options,
  onCreateOption,
  placeholder,
  hideLabel = false,
  createText,
  disabled = false,
}: FormSelectCreatableProps<T>) => {
  const [inputValue, setInputValue] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isAdding && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isAdding]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;

    // If the user selects the "add new" option
    if (val === '__new__') {
      setIsAdding(true);
      return;
    }

    onChange(val as T);
  };

  const handleAddNew = () => {
    if (!inputValue.trim()) {
      setIsAdding(false);
      return;
    }

    if (onCreateOption) {
      onCreateOption(inputValue);
    }

    onChange(inputValue as T);
    setInputValue('');
    setIsAdding(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddNew();
    } else if (e.key === 'Escape') {
      setIsAdding(false);
      setInputValue('');
    }
  };

  return (
    <div>
      {labelElement ? (
        labelElement
      ) : (
        <label
          className={
            hideLabel ? 'sr-only' : 'block text-sm font-medium text-gray-700 dark:text-gray-200'
          }
        >
          {label}
        </label>
      )}

      {isAdding ? (
        <div className="mt-1 flex rounded-md shadow-sm">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="block w-full rounded-l-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm"
            placeholder={createText ?? t('common.add')}
          />
          <button
            type="button"
            onClick={handleAddNew}
            className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-600 dark:text-white dark:hover:bg-gray-500"
          >
            {createText ?? t('common.add')}
          </button>
        </div>
      ) : (
        <select
          value={value}
          onChange={handleChange}
          disabled={disabled}
          className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:hover:border-gray-500 sm:text-sm"
          aria-label={hideLabel ? label : undefined}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((option) => (
            <option key={option.id} value={option.name}>
              {option.name}
            </option>
          ))}
          {onCreateOption && <option value="__new__">{createText ?? t('common.add')}</option>}
        </select>
      )}
    </div>
  );
};
